import { GeoJsonTypes } from "geojson";

export interface InRangeBaseModel {
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  updatedBy?: string;
}

export interface DnoData {
  name: string;
  annualAverageIntensity: number;
}

export interface Substation {
  success: boolean;
  dno?: string; // DNO operator
  gsp_name?: string; // Grid Supply Point name
  bsp_name?: string; // Bulk Supply Point name
  name: string; // Primary substation name
  latitude?: number;
  longitude?: number;
  headroom?: number; // Mva
  scenario_name?: string;
  scenario_year?: string;
  load_date?: string;
  match_type?: string;
}

export interface SiteOwnership {
  orgID: string;
  ownership: string;
  siteID?: string;
  name?: string;
  isTest?: boolean;
  park?: string;
  spv?: string;
}

export interface CostInputsPv {
  // Upfront costs
  enablingWorksCosts: number;
  wiringCosts: number;
  hardwareCostPerKWp: number;
  installationCostPerKWp: number;
  contingency: number;
  capitalisedCost: number;
  capitalisedInterest: number;

  // Ongoing costs
  maintenanceCostPerKWp: number;
  insuranceCostPerKWp: number;
  flatAnnualCost: number;
  roofRentPerM2: number;
  roofRentBtm: number;
  roofRentExport: number;
  contingencyOpex: number;

  // Replacement costs
  replacementCapexYear: number;
  replacementCapexRate: number;
}

export interface BatteryInstall {
  modelName: string;
  count: number;
  totalCapacityMwh: number;
}

export interface BatterySummary {
  batteryInstalls: BatteryInstall[];
  capacityMwh: number;
  powerMw: number;
}

export interface BatterySpec {
  modelName?: string | null;
  modelNotes?: string;
  capacityMwh: number;
  powerMw: number;
  depthOfDischarge?: number;
  roundTripEfficiency?: number;
  warranty?: {
    periodYears?: number;
    periodCycles?: number;
    capacity?: number;
  };
  defaultCost: LocalBatteryCost;
  defaultMfrCost?: LocalBatteryCost | null;
  deprecated?: boolean;
  count?: number;
}

export interface BatteryCost {
  installationCost: number;
  hardwareCost: number;
}

export interface LocalBatteryCost extends BatteryCost {
  currency: string;
}

export interface CostInputsBattery {
  costs: { [cost_key: string]: BatteryCost };
  simVersion: string;
}

export interface CostInputsInrange {
  // > InRange construction arbitrage
  enablingWorksCosts?: number;
  epcCostPerKWp?: number;
  freeIssueCostPerKWp?: number;
  batteryCosts: { [key: string]: BatteryCost };

  // > InRange Lease
  constructionMonths: number;
  equityFraction: number;
  interestRateConstruction: number;
  interestRateOperationalFixed: number;
  interestRateOperationalFixedTermYears: number;
  interestRateOperationalVariable: number;
  loanTermYears: number;
  vatRate: number;
  corporateTaxRate: number;
  commitmentFeeRate: number;
  arrangementFeeRate: number;
  annualMonitoringFeeRate: number;
  targetDscr: number;
  targetDscrMin: number;
  targetDscrAvg: number;
  discountRate: number;
  targetLlcrMin: number;
  shapeInterestPayments: boolean;
  debtPaybackDividendRate: number;
  includeAllCosts: boolean;
  accountingFee: number;
  assetMgmtFee: number;
  ppaMgmtFee: number;
  legalFees: number;
  modelingFees: number;
  arbitrageOverride?: number;
  leaseNetArrOverride?: number;
}

export interface HalfHourlyDemand {
  source: string;
  metadata?: {
    latest_full_year_num_rows_customer_data: number;
    latest_full_year_num_rows: number;
    input_file?: string;
    date_of_analysis?: string;
    input_data_start?: string;
    input_data_end?: string;
    fullyear_data_start?: string;
    fullyear_data_end?: string;
    largest_gap_days?: number;
    site_mapping?: {
      overrides?: {
        existing_panels?: number;
      };
    };
  };
}

export interface ComputedLosses {
  generationLossFactors: {
    [factorKey: string]: number | undefined;
  };
}

export interface GenerationFactor {
  factor: string;
  pct: number;
  computed: boolean;
  after: number;
}

export interface GenerationStack {
  inputGen: number;
  stack: GenerationFactor[];
}

export interface HalfHourlyGeneration {
  kwpPerSqm: number;
  roofUtilization: number;
  locationGhi: number;
  ghi: number;
  yoyVarInvLossFactor: number;
  genStack: GenerationStack;
  genStackMonthly: { [month: number]: GenerationStack };
}

export interface AggregateHalfHourlyGeneration {
  yoyVarInvLossFactor: number;
  genStack: GenerationStack;
  genStackMonthly: { [month: number]: GenerationStack };
}
export interface SdmMatchConfig {
  // >> Buyer/seller
  buyerId: string;
  buyerName?: string;
  buyerEsysUuid: string;
  sellerId: string;
  sellerName?: string;
  sellerEsysUuid: string;

  // >> Match config
  tariff: number;
  volume: number;
  ppaContractType: string;
  ppaLength: number;
  ppaIndex: number;
  isWired: boolean;

  // >> Energy flow storage information
  energyFlowInputsFileVersionId?: string;

  // >> State to allow us to detect when a match curve is stale
  existingMatchesHash?: string;
  existingMatchesHashStale?: boolean;
}

export interface EnergyMatchConfig {
  tariff: number;
  ppaContractType: string;
  ppaPurchaseModel: string;
  ppaLeaseBillingFrequency: string;
  ppaCrossPpaIndexationType: string;
  ppaLength: number;
  ppaType: string;
  ppaIndex: number;
  nextPpa: EnergyMatchConfig | undefined;
}

export interface TimedTariffAggregateEnergyFlow {
  tariffName: string;
  rateName: string;
  demand: number;
  grid: number;
  networkImport: number;
  behindMeter: number;
  behindMeterGrid: number;
}
export interface AggregateMatchedEnergy {
  sourceType: string;
  sinkType: string;
  energyFlow: number;
  energyMatchConfig: EnergyMatchConfig;
  sinkId?: string;
  sourceId?: string;
}

export interface AggregateEnergyFlow {
  year: number;
  month: number;

  demand: number;
  generation: number;
  batteryCharge: number;
  batteryDischarge: number;
  batteryChargeLoss: number;

  batteryGridCharge: number;
  batteryGridDischarge: number;
  batteryGridChargeLoss: number;

  batteryMaxFinalCharge: number;
  behindMeter: number;
  behindMeterPV: number;
  behindMeterBattery: number;
  behindMeterBatteryGrid: number;
  exported: number;
  exportedPV: number;
  exportedBattery: number;
  curtailed: number;
  networkImport: number;
  networkExport: number;
  gridImport: number;
  procurement: number;

  timedImportTariffs: TimedTariffAggregateEnergyFlow[];

  daylightDemand?: number;
  daylightGrid?: number;
  daylightInRange?: number;
  daylightBehindMeter?: number;

  tenantEnergyShare: number;

  maxExport: number;

  matchedEnergy: AggregateMatchedEnergy[];

  checks: {
    checkPv: number;
    checkBatteryDischargePv: number;
    checkBatteryDischargeGrid: number;
    checkNetworkImport: number[];
    checkDemand: number;
    checkBatteryChargePv: number;
    checkBatteryChargeGrid: number;
    checkNetworkExport: number[];
  };
}

export interface EnergyMonthActualsMetadata {
  first_day: string; // Assuming the date is returned as a string
  last_day: string; // Assuming the date is returned as a string
}

export interface EnergyMonth {
  forecast?: AggregateEnergyFlow;
  actual?: AggregateEnergyFlow;
}

export interface FinancialMonth {
  forecast?: FinancialPerformanceScenarios;
  actual?: FinancialPerformanceScenarios;
}

export interface EnergyFinancialMonth {
  date: string;
  month: number;
  energy: EnergyMonth;
  financial: FinancialMonth;
  actualMetadata?: EnergyMonthActualsMetadata;
}

export interface HalfHourlyEnergyFlow {
  // >> Energy Sources
  generation: number[];
  // >> Energy Sinks
  demand: number[];
  // >> Energy matches
  behindMeterPV: number[];
  behindMeterBattery: number[];
  behindMeterBatteryGrid: number[];
  networkImport: number[];
  networkExport: number[];
  exportedPV: number[];
  exportedBattery: number[];
  // >> Energy matches: Excess energy
  curtailed: number[];
  gridImport: number[];
}

export interface ProjectCosts {
  totalCostPerKwp: number;
  initialCostPerKWp: number;

  total: number;
  initialInvestment: number;

  // InRange cost breakdown (EPC-only + free-issue + InRange/mfr battery)
  epcOnlyCost: number;
  freeIssueCost: number;
  totalCostToInRange: number;
  totalInRangeCostPerKwp: number;

  // Hardware costs
  pvHardware: number;
  batteryHardware: number;
  totalHardware: number;

  // Install costs
  pvInstallation: number;
  batteryInstallation: number;
  totalInstallation: number;

  // Ongoing costs
  maintenance: number;
  insurance: number;
  additionalCosts: number;
  maintenanceYearOne: number;
  insuranceYearOne: number;

  // Replacement costs
  pvReplacementCapex: number;

  // Battery costs
  batteryReplacementCapex: number;
}

export interface FinancialModelProcurement {
  // Tenant procurement costs
  costTotal: number;
  costOnsitePV: number;
  costOnsiteBattery: number;
  costOnsiteTariffArbitrage: number;
  costNetworkImport: number;

  // Tenant savings
  savings: number;
  savingsPerPpa: number[];
  lifetimeSavings?: number | null;
}

export interface FinancialModelLease {
  revenue: number;
  lifetimeRevenue: number;
}

export interface FinancialModelLicense extends FinancialModelLease {
  inrangeNetRevenue: number;
  irr?: number;
  paybackMonths?: number;
}

export interface LeaseLicenseInvestmentModels {
  lease: FinancialModelLease;
  license: FinancialModelLicense;
}

export interface FinancialModelScenarios {
  landlord: LeaseLicenseInvestmentModels;
  ownerOccupier: LeaseLicenseInvestmentModels;
  marketPrice: number;
  marketPricePerPpa: number[];
  procurement: FinancialModelProcurement;
  ooLicenseProcurement: FinancialModelProcurement;
}

export interface FinancialModelInRangeLease {
  netRevenue: number;
  irr?: number;
  paybackMonths?: number;
  dscrMin: number;
  dscrAvg: number;
  llcrMin: number;
}

export interface FinancialModelGrossRevenue {
  grossEnergyRevenue: number;
  grossEnergyRevenueOnsite: number;
  grossEnergyRevenueExport: number;
}

export interface FinancialModelGrossTariffArbitrageRevenue {
  grossRevenue: number;
  grossProcurementValue: number;
}

export interface FinancialModelInRangeScenarios {
  defaultRevenue: FinancialModelGrossRevenue;
  ooLicenseRevenue: FinancialModelGrossRevenue;
  tariffArbitrageRevenue: FinancialModelGrossTariffArbitrageRevenue;
  installArbitrage: number;
  equityInjected: number;
  lease: FinancialModelInRangeLease;
  takeOrPayNonMonetisedExportKwh: number;
}

export interface FinancialPerformanceProcurement {
  // Tenant procurement costs
  costTotal: number;
  costOnsitePV: number;
  costOnsiteBattery: number;
  costOnsiteTariffArbitrage: number;
  costNetworkImport: number;

  // Tenant savings
  savings: number;
}

export interface FinancialPerformance {
  revenueTotal: number;
  revenueOnsitePV: number;
  revenueOnsiteBattery: number;
  revenueOnsiteTariffArbitrage: number;
  revenueExport: number;
}

export interface FinancialPerformanceInvestmentModels {
  lease: FinancialPerformance;
  license: FinancialPerformance;
}

export interface FinancialPerformanceScenarios {
  landlord: FinancialPerformanceInvestmentModels;
  ownerOccupier: FinancialPerformanceInvestmentModels;
  procurement: FinancialPerformanceProcurement;
  ooLicenseProcurement: FinancialPerformanceProcurement;
}

export interface HubSpotSite {
  siteID: string;
  hubspotID: string;
  createdBy: string;
  hubspotUpdateTime?: number;
}

export interface SiteFile extends InRangeBaseModel {
  siteID: string;
  fileType: string;
  fileName: string;
  fileGuid: string;
  fileVersionId: string | null;
  date?: number;
  isLandlord?: boolean;
  description?: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  invoiceDueDate?: number;
  statementNumber?: string;
  statementAmount?: number;
  issueDate?: number;
  invoiceOtherCharges?: Array<{
    description: string;
    amount: number;
    type: "charge" | "consumption_adjustment";
  }>;
  isPublished: boolean;
  siteOwnerships: SiteOwnership[];
  isInternal?: boolean;
  isQuery?: boolean;
  buyerId?: string;
  buyerEsysUuid?: string;
  sellerId?: string;
  sellerEsysUuid?: string;
}

export interface PvPanel {
  manufacturer: string;
  model: string;
  width: number;
  height: number;
  weight: number;
  pnom: number;
  deprecated: boolean;
  warranty: {
    yearOneEfficiency: number;
    periodYears: number;
    endOfPeriodEfficiency: number;
  };
}

export interface Building extends InRangeBaseModel {
  id: string;
  siteID: string;
  geometry: {
    type: GeoJsonTypes;
    coordinates: string | number[][][];
  };
  surfaceAreaSqM: number;
  polygonType?: string;
}

export const getBuildingCoordinates = (building: Building): number[][][] => {
  let coordinates = building.geometry.coordinates;
  if (typeof building.geometry.coordinates === "string") {
    coordinates = JSON.parse(building.geometry.coordinates);
  }
  return coordinates as unknown as [number, number][][];
};

export interface Calculations {
  emissionsAvoided: { totalAvoidance: number };
}

export enum TimedTariffDays {
  EVERY_DAY = "everyDay",
  WEEKDAYS = "weekdays",
  EXCLUDE_SUNDAYS = "excludeSundays",
  WEEKENDS = "weekends",
}

export enum TariffPoint {
  MSP = "MSP", // Meter supply point
  GSP = "GSP", // Grid supply point
  NBP = "NBP", // Network boundary point
}

export interface TimedTariffRatePeriod {
  days: string; // TimedTariffDays
  startIndex: number; // e.g., 14 (7:00)
  endIndex: number; // e.g., 45 (22:30)
  startYearDay?: number;
  endYearDay?: number;
}

export interface TimedTariffRate {
  id: string;
  name: string;
  tariff?: number; // If undefined, means this item is turned off
  rangesMap: { [key: string]: TimedTariffRatePeriod };
}

export enum TariffType {
  CONTRACT = "contract",
  MARKET = "market",
}

export interface TimedTariff {
  tariffName: string;
  tariffPoint: string; // TariffPoint
  tariffType: string; // TariffType
  baseRate: TimedTariffRate;
  overrideRatesMap: { [key: string]: TimedTariffRate };
  applyEnergyInflation: boolean;
}

export enum TariffBillingType {
  MONTHLY_ACTUALS = "monthlyActuals",
  ANNUAL_ESTIMATE = "annualEstimate",
}

export interface SingleRateTimedTariff {
  tariffName: string;
  tariffPoint: string; // TariffPoint
  rate: TimedTariffRate;
  billingType: string; // TariffBillingType
}

export interface FixedTariff {
  tariffName: string;
  volume?: number;
  tariff: number;
  tariffPoint?: string; // TariffPoint
  isLossesCharge: boolean;
}

export enum StandingChargeType {
  FIXED_VALUE = "fixedValue",
  PER_DAY = "perDay",
  PER_MONTH = "perMonth",
}

export interface StandingCharge {
  chargeName: string;
  volume?: number;
  tariff: number;
  chargeType: StandingChargeType;
}

export interface SupplierTariffConfig {
  peakOffPeak: TimedTariff;
  duos: TimedTariff;
  standardSingleRateTimedTariffs: { [key: string]: SingleRateTimedTariff };
  tariffPointFactorGsp?: number;
  tariffPointFactorNbp?: number;
  standardFixedTariffs: { [key: string]: FixedTariff };
  customFixedTariffs: FixedTariff[];
  standardStandingCharges: { [key: string]: StandingCharge };
  customStandingCharges: StandingCharge[];
  // Other attributes
  supplier: string;
  voltage: string;
  contractType: string; // ContractType
  contractLength: number;
  contractIndexType: number; // PpaType
  contractIndex: number;
  contractEnergyPriceInflationRate: number;
  duosInflationRate: number;
  ppaStartPoint: number;
  // Grid data lookup config
  substationVersion: string;
}

export interface TariffConfig {
  arbitrageTenantShare: number;
  arbitrageLeaseRevShare: number;
  arbitrageLicenseRevShare: number;
  arbitrageEnabled: boolean;

  takeOrPay: boolean;
  takeOrPayMinKwh: number;

  onsitePPA: EnergyMatchConfig;
  spillExportPPA: EnergyMatchConfig;
  networkImportTariff: number;
  actualSpillTariff?: number;
  // Whether the site physically enforces zero grid export, independent of the export limit
  // used for financial modelling and of export monetisation (actualSpillTariff). Drives
  // curtailment detection (treats the export limit as 0 when true).
  zeroExportEnforced?: boolean;

  supplier: SupplierTariffConfig;
}

export const getOnsitePpaLengthsConfig = (site: Site): EnergyMatchConfig => {
  if (
    site.totalInstalledCapacity === 0 &&
    site.sdmMatches.filter((match) => match.buyerId === site.id).length > 0
  ) {
    // Procure only site with an applied SDM buy match, compute the effective EnergyMatchConfig
    const buyMatches = site.sdmMatches.filter(
      (match) => match.buyerId === site.id
    );
    const shortestBuyMatch = buyMatches.reduce((prev, curr) =>
      curr.ppaLength < prev.ppaLength ? curr : prev
    );
    return {
      tariff: shortestBuyMatch.tariff,
      ppaContractType: shortestBuyMatch.ppaContractType,
      ppaPurchaseModel: "as_produced",
      ppaLeaseBillingFrequency: "quarterly",
      ppaCrossPpaIndexationType: "compound",
      ppaLength: shortestBuyMatch.ppaLength,
      ppaType: shortestBuyMatch.ppaIndex === 0 ? "fixed" : "indexed",
      ppaIndex: shortestBuyMatch.ppaIndex,
      nextPpa: undefined,
    };
  }
  return site.tariffConfig.onsitePPA;
};

export const getPpas = (
  ppa: AllowStringForNumbers<EnergyMatchConfig>,
  defaultPpaContractType: string
): Partial<AllowStringForNumbers<EnergyMatchConfig>>[] => {
  const ppas: Partial<AllowStringForNumbers<EnergyMatchConfig>>[] = [];
  let currentPpa: AllowStringForNumbers<EnergyMatchConfig> | undefined = ppa;
  while (currentPpa) {
    ppas.push(currentPpa);
    currentPpa = currentPpa.nextPpa;
  }
  if (ppas.length === 0) {
    ppas.push({
      tariff: undefined,
      ppaContractType: defaultPpaContractType,
      ppaLength: undefined,
      ppaType: "fixed",
      ppaIndex: undefined,
      nextPpa: undefined,
    });
  }
  return ppas;
};

export const ppaUpdate = (
  index: number,
  ppa: RecursivePartial<AllowStringForNumbers<EnergyMatchConfig>>
) => {
  let result = ppa;
  for (let i = 0; i < index; i++) {
    result = {
      nextPpa: result as unknown as AllowStringForNumbers<
        EnergyMatchConfig | undefined
      >,
    };
  }
  return result;
};

export const ppaErrorString = (index: number) => {
  let result = "";
  for (let i = 0; i < index; i++) {
    result += ".nextPpa";
  }
  return result;
};

export const ppaStartingTariff = (
  lastPpaStartingTariff: number,
  lastPpaLength: number,
  energyInflationRate: number,
  crossPpaIndexationType: string
) => {
  const inflation =
    crossPpaIndexationType === "compound"
      ? Math.pow(1 + energyInflationRate, lastPpaLength)
      : 1 + energyInflationRate;
  return Math.round(inflation * lastPpaStartingTariff * 1000.0) / 1000.0;
};

export interface SiteInvoiceProps {
  tenantLegalEntityName?: string | null;
  tenantEntityAddress?: string | null;
  landlordLegalEntityName?: string | null;
  landlordEntityAddress?: string | null;
  bankAccountId?: string | null;
  termsAndConditionsId?: string | null;
  invoiceFooterTextId?: string | null;
  vatNoticeId?: string | null;
  invoiceQuestionsId?: string | null;
}

export interface SiteFeatureFlags {
  // Added as part of https://github.com/InRange-io/building-manager-api/pull/666
  dstShiftDemand?: boolean;
  cplVersion?: string;
  tmyFilterOutliers?: boolean;
}

export interface SiteElectricalSystemPlaceholder {
  idx: string;
  genIdx: string;
  demandIdx: string;
  batteryIdx: string;
}

export interface SiteElectricalSystem {
  uuid: string;
  idx: number;
  genIdx: number;
  demandIdx: number;
  batteryIdx: number;

  // SDM match state
  sdmInputsDataHash?: string;

  // Grid connection
  exportGridConnectionId?: string;
  exportMeterId?: string;
  importGridConnectionId?: string;
  importMeterId?: string;
  exportLimit?: number;

  // >> Energy flow outputs
  energyFlowAnnual?: AggregateEnergyFlow;
  energyFlowMonthly?: {
    [month: number]: AggregateEnergyFlow;
  };
}

export interface SiteDemand {
  uuid: string;
  idx: number;
  areaPct: number;
  demandCoefficientKWhPerM2: number;
  buildingProfile: string;
  profileShape: string;
  profileDays: string;
  existingPvKwp?: number;
  hhDemandFileDescription: string;
  hhDemandGuid?: string;
  halfHourlyDemand?: HalfHourlyDemand;

  // Demand forecast shape over the system lifetime. "flat" keeps demand at the
  // year-1 level; "changing" steps demand by demandChangePercent (a fraction,
  // e.g. 0.1 or -0.03) every demandChangeEveryNYears years.
  demandForecast: string;
  demandChangePercent: number;
  demandChangeEveryNYears: number;

  // Property
  s3DemandSuffix: string;
}

export interface PanelOrientation {
  tilt: number;
  azimuth: number;
  panelCount: number;
}

export interface SiteGeneration {
  idx: number;
  uuid: string;

  // Generation
  installedCapacity: number;
  panelOrientations: PanelOrientation[];

  // Loaded half hourly generation data
  halfHourlyGeneration?: HalfHourlyGeneration;

  // Inverters
  inverterConfig: string;
  inverterTargetAcDcRatioMin: number;
  inverterTargetAcDcRatioMax: number;
  inverters: InstalledInverter[];
  invertersOptimal: InstalledInverter[];
}

export interface SiteBattery {
  idx: number;
  uuid: string;

  batterySpec?: BatterySpec;
  batteryLastPriceInputs?: string;
  batteryEnergyFlowPayloadHash?: string;
  batteryEnergyFlowFileVersionId?: string;
  batteryCostKey?: string;
}

export interface SiteLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  postcode?: string;
  totalInstalledCapacity?: number;
  [key: string]: any;
}

// Type used for creating a site - all fields are optional except for id
export type NewSite = RecursivePartial<Site>;

// Type used for previewing a site - all fields are optional
export type PartialSite = Partial<Site>;

export interface Site extends InRangeBaseModel {
  id: string;

  locked: boolean;
  // The lead this site was created from, by promote or Create site. Null for
  // sites made any other way.
  originatedFromLeadId?: string | null;
  lockNote: string;

  // >> Basic attributes
  name: string;
  address: string;
  postcode: string;
  notes: string;
  latitude: number;
  longitude: number;
  mapZoom: number;
  sdmExcludeFromMatching: boolean;
  sdmHideMatches: boolean;
  copiedFromSiteID?: string;
  unlockedSiteID?: string;
  lockedSiteID?: string;
  score?: number | null;
  scoreFactors?: ScoreFactor[];
  suggestedScore?: number | null;
  suggestedScoreFactors?: ScoreFactor[];

  // >> Location linked data
  countryCode: string;
  timeZone: string;
  currencyCode: string;
  areaUnit: string;
  dno?: DnoData;
  substationData?: Substation;

  // >> Buildings
  buildings: Building[];
  totalBuildingArea: number;

  // >> Reviews
  // Generic operator review state keyed by review kind. A pvLayout review
  // is stale once a newer boundary review supersedes it (pvLayout.at <
  // boundary.at). Stored as a dict so partial updates deep-merge BMA-side.
  reviews?: {
    boundary?: { at?: number; by?: string };
    pvLayout?: {
      at?: number;
      by?: string;
      quality?: "high" | "medium" | "low";
    };
  };

  // >> Proximity
  nearbySiteIDs: string[];

  // >> Ownership
  siteOwnerships: SiteOwnership[];

  // >> Esys / Generation / Demand / Battery
  esys: { [match_index: number]: SiteElectricalSystem };
  demand: { [demand_index: number]: SiteDemand };
  generation: { [generation_index: number]: SiteGeneration };
  battery: { [battery_index: number]: SiteBattery };

  // >> Site summary stats
  tenantAnnualDemandKwh: number;
  totalInstalledCapacity: number;
  totalRoofUtil: number;
  totalInverters: InstalledInverter[];
  totalExportLimit?: number;
  totalBatterySummary: BatterySummary | undefined;
  hasAnyZeroExportLimit: boolean;
  hasAllZeroExportLimit: boolean;
  // Effective zero export (folds in tariffConfig.zeroExportEnforced): true when the
  // site exports nothing in practice, even if modelled with a non-zero export limit.
  // Use these (not the *Limit variants) for actuals / health / chart surfaces.
  hasAnyZeroExport: boolean;
  hasAllZeroExport: boolean;

  // Site level generation settings
  aggHalfHourlyGeneration: AggregateHalfHourlyGeneration;
  generationLossFactors: {
    [factorKey: string]: number | undefined;
  };
  generationLossFactorsOverride: {
    [factorKey: string]: boolean | undefined;
  };
  pvPanelName: string;
  pvDesignSystemLastUpdatedAt?: number;
  pvDesignSystemLastUpdatedBy?: string;
  annualGenerationLoss: number;
  pvInverterBrand: string;
  measurementType: string;
  costInputsPv: CostInputsPv;
  pvPanelCount: number;
  pvPanel: PvPanel;
  pvCurve: string;
  pvForecastProbability: string;
  ghiSource: string;
  overrideGHI?: number;
  weatherVersionTimestamp?: number;
  pvYoyForecastVariability: number;

  // >> Battery costs
  costInputsBattery: CostInputsBattery;

  // >> SDM matches
  sdmMatches: SdmMatchConfig[];

  // >> Energy flow outputs
  energyFlowAnnual: AggregateEnergyFlow;
  energyFlowMonthly: {
    [month: number]: AggregateEnergyFlow;
  };
  energyFlowSummerDay: HalfHourlyEnergyFlow;
  energyFlowWinterDay: HalfHourlyEnergyFlow;

  // >> Operational attributes
  modellingFromDate: string;
  commerciallyOperationalDate?: string | null;
  funderStartDate?: string | null;
  operationalStatus: string;
  oAndMContactEmail?: string | null;

  // >> Tariffs
  tariffConfig: TariffConfig;
  blendedExportTariff: number;

  // >> Deal and contract attributes
  leaseLengthDate?: string | null;
  leaseLengthMonths?: number;
  systemLifetimeYears: number;
  vacancyLossRate: number;
  oAndMCostMode: string;

  // >> Financial modeling - inputs
  landlordLeaseRevenueShare: number;
  landlordLicenseRevenueShare: number;
  ownerOccupierLicenseRevenueShare: number;
  ownerOccupierLicenseInRangeSaasTariffRate: number;
  energyPriceInflationRate: number;
  costInputsEconomic: { costInflationRates: number[] };
  costInputsInrange?: CostInputsInrange;
  investmentModel: string;
  activeInvestmentModel: "lease" | "license";

  // >> Financial modeling - outputs
  projectCosts: ProjectCosts;
  financialModels: FinancialModelScenarios;
  financialModelsInrange: FinancialModelInRangeScenarios;
  energyFinancialsMonthly: EnergyFinancialMonth[];

  // >> Marketplace
  minMarketplaceMatchSize: number;
  maxWiredDistanceKm: number;

  // >> Files
  siteFiles: SiteFile[];
  calculations: Calculations;

  // >> Invoice properties
  invoiceProps?: SiteInvoiceProps | null;

  // >> Feature flags
  flags: SiteFeatureFlags;
}

export type RecursivePartial<T> = {
  [K in keyof T]?: T[K] extends object
    ? RecursivePartial<T[K]>
    : T[K] extends object | undefined
      ? RecursivePartial<T[K]> | undefined
      : T[K];
};

export type AllowStringForNumbers<T> = {
  [K in keyof T]: T[K] extends number
    ? T[K] | string
    : T[K] extends number | undefined
      ? T[K] | string
      : T[K] extends number[]
        ? T[K] | string[]
        : T[K] extends object
          ? AllowStringForNumbers<T[K]>
          : T[K] extends object | undefined
            ? AllowStringForNumbers<T[K]> | undefined
            : T[K];
};

export type SiteAllowStringValues = AllowStringForNumbers<Site>;

export type PartialSiteAllowStringValues = Partial<AllowStringForNumbers<Site>>;
export type RecursivePartialSiteAllowStringValues = RecursivePartial<
  AllowStringForNumbers<Site>
>;

export type NullableAllowStringForNumbers<T> = {
  [K in keyof T]: T[K] extends number | undefined
    ? T[K] | string | null
    : T[K] extends number
      ? T[K] | string | null
      : T[K] | null;
};

export type ChangeToSiteDefaults = {
  title: string;
  beforeDefaults: PartialSiteAllowStringValues;
  afterDefaults: PartialSiteAllowStringValues;
};

export interface NetworkSite {
  id: string;
  latitude: number;
  longitude: number;
  outcode?: string;
  networkImportTariff: number;
  isNetwork?: boolean;
  energyFlowAnnual: AggregateEnergyFlow;
  currencyCode: string;
}

export interface SdmOffer {
  financialModels: FinancialModelScenarios;
  config: SdmMatchConfig;
  dest: {
    id: string;
    dno: string;
    sub: string;
    km: number;
    from_additional_org: boolean;
    sdm_excluded: boolean;
    is_same_park: boolean;
    minMarketplaceMatchSize: number;
    gridImport: number;
    exported: number;
    latitude?: number;
    longitude?: number;
  };
  emissionsAvoidedYearOne: number;
}

export interface SdmSiteOffer {
  src: {
    esysName?: string;
  };
  dest: {
    id: string;
    name: string;
    esysName?: string;
    dno: string;
    sub: string;
    km: number;
    orgs: string[];
    from_additional_org: boolean;
    sdm_excluded: boolean;
    is_same_park: boolean;
    minMarketplaceMatchSize: number;
    gridImport: number;
    exported: number;
  };
  sell: {
    financialModels: FinancialModelScenarios;
    config: SdmMatchConfig;
  };
  buy: {
    financialModels: FinancialModelScenarios;
    config: SdmMatchConfig;
    emissionsAvoidedYearOne: number;
  };
}

export interface EnergyFlowGraphData {
  summerDay: number[];
  winterDay: number[];
  monthly: { [key: string]: number };
}

export interface OfferEnergyFlows {
  match: EnergyFlowGraphData;
  total: EnergyFlowGraphData;
}

export interface SitePreviewError {
  error: string;
  invalid_states: { loc: string[]; msg: string; type: string }[];
}

export interface SiteCalculations {
  // >> Location linked data
  latitude: number;
  longitude: number;
  countryCode: string;
  timeZone: string;
  currencyCode: string;
  areaUnit: string;
  dno?: DnoData;
  substationData?: Substation;
  // >> Buildings
  totalBuildingArea: number;
  // >> Esys / Generation / Demand / Battery
  esys: { [match_index: number]: SiteElectricalSystem };
  demand: { [demand_index: number]: SiteDemand };
  generation: { [generation_index: number]: SiteGeneration };
  battery: { [battery_index: number]: SiteBattery };
  tenantAnnualDemandKwh: number;
  // >> Site summary stats
  totalInstalledCapacity: number;
  totalRoofUtil: number;
  totalInverters: InstalledInverter[];
  totalExportLimit?: number;
  totalBatterySummary: BatterySummary | undefined;
  hasAllZeroExportLimit: boolean;
  // >> Site level generation settings
  aggHalfHourlyGeneration?: AggregateHalfHourlyGeneration;
  pvPanelCount: number;
  generationLossFactors?: {
    [factorKey: string]: number | undefined;
  };
  costInputsPv?: CostInputsPv;
  // >> Battery costs
  costInputsBattery?: CostInputsBattery;
  // >> Energy flow outputs
  energyFlowAnnual?: AggregateEnergyFlow;
  energyFlowMonthly?: {
    [month: number]: AggregateEnergyFlow;
  };
  // >> Tariffs
  blendedExportTariff: number;
  // >> Deal and contract attributes
  leaseLengthMonths: number;
  // >> Financial modeling - inputs
  activeInvestmentModel: "lease" | "license";
  // >> Financial modeling - outputs
  projectCosts?: ProjectCosts;
  financialModels?: FinancialModelScenarios;
  financialModelsInrange?: FinancialModelInRangeScenarios;
  // >> Site score
  suggestedScore?: number | null;
  suggestedScoreFactors?: ScoreFactor[];
  // Extra attributes
  updatedAt?: number;
}

export type ScoreFactorStatus = "positive" | "negative" | "neutral" | "skipped";

export interface ScoreFactor {
  key: string;
  label: string;
  status: ScoreFactorStatus;
  message: string;
  contribution: number;
}

export type EditMode = "SINGLE" | "ONE_OF_MULTIPLE" | "SUMMARY";

export interface SiteEnergyFlowGraphData {
  match: EnergyFlowGraphData;
  total?: EnergyFlowGraphData;
}

export interface SiteMarketplaceGraphs {
  buy: {
    [key: string]: SiteEnergyFlowGraphData;
  };
  sell: {
    // No "total" for sell matches
    [key: string]: SiteEnergyFlowGraphData;
  };
}

export interface InverterWarranty {
  periodYears: number;
}

export interface PvInverter {
  manufacturer: string;
  model: string;
  maxDcPower: number;
  ratedAcOutputPower: number;
  warranty: InverterWarranty;
}

export interface InstalledInverter {
  name: string;
  count: number;
}

export interface PvSystem {
  generationLossFactors: {
    [factorKey: string]: number | undefined;
  };
  costInputsPv: CostInputsPv;
}

export interface SiteValuesTariffs {
  marketTariff: number;
  tenantTariff: number;
  inrangeExportTariff: number;
}

export interface SiteValuesBuildingProfileData {
  name: string;
  demandCoefficient: number;
  profileShape: string;
  profileDays: string;
  defaultMarketTariff: number;
  maxTenantTariff: number;
  maxNetworkImportTariff: number;
}

export interface PvCurve {
  value: string;
  label: string;
}

export interface GhiSource {
  value: string;
  label: string;
}

export interface SiteValuesOptions {
  pvCurves: Record<string, PvCurve>;
  ghiSources: Record<string, GhiSource>;
  pvPanels: Record<string, PvPanel>;
  pvInverterBrands: string[];
  pvInverters: Record<string, PvInverter>;
  pvSystems: Record<string, PvSystem>;
  batteries: Record<string, Record<string, BatterySpec>>;
  tariffsDatas: Record<string, SiteValuesTariffs>;
  pvLossFactorLabels: Array<{
    label: string;
    key: string;
    deprecated?: boolean;
  }>;
  buildingProfiles: Record<string, SiteValuesBuildingProfileData>;
}

export interface SiteChangeLog {
  siteID: string;
  time: number;
  sortTime: number;
  updatedBy: string;
  userNote?: string;
  userChange: Record<string, { before: any; after: any }>;
  recalculated: Record<string, { before: any; after: any }>;
}

export interface SiteLogPreviewResponse {
  site_id: string;
  log: SiteChangeLog | null;
}

export const getFinancialPerformanceProcurement = (
  scenarios: FinancialPerformanceScenarios | undefined,
  isOwnerOccupier: boolean,
  activeInvestmentModel: string
): FinancialPerformanceProcurement | undefined => {
  if (!scenarios) {
    return undefined;
  }
  return isOwnerOccupier && activeInvestmentModel === "license"
    ? scenarios.ooLicenseProcurement
    : scenarios.procurement;
};

export const getFinancialPerformance = (
  scenarios: FinancialPerformanceScenarios | undefined,
  isOwnerOccupier: boolean,
  activeInvestmentModel: string
): FinancialPerformance | undefined => {
  if (!scenarios) {
    return undefined;
  }
  if (isOwnerOccupier) {
    return scenarios.ownerOccupier[
      activeInvestmentModel as keyof LeaseLicenseInvestmentModels
    ];
  }
  return scenarios.landlord[
    activeInvestmentModel as keyof LeaseLicenseInvestmentModels
  ];
};

export const smartAlphaNumericSortInstalledInverters = (
  a: InstalledInverter,
  b: InstalledInverter
) => {
  return smartAlphaNumericSort(a.name, b.name);
};

export const smartAlphaNumericSort = (a: string, b: string) => {
  const [prefixA, numA] = extractPrefixAndNumber(a);
  const [prefixB, numB] = extractPrefixAndNumber(b);

  if (prefixA === prefixB && numA !== null && numB !== null) {
    return numB - numA;
  }

  return a.localeCompare(b);
};

// Extracts [prefix, number] where number is the numeric suffix after the last run of digits
function extractPrefixAndNumber(str: string): [string, number | null] {
  const match = str.match(/^([^\d]*)(\d+(?:\.\d+)?)/);
  if (match) {
    return [match[1], parseFloat(match[2])];
  }
  return [str, null];
}

export const getESysName = (
  esysCount: number,
  esys:
    | AllowStringForNumbers<SiteElectricalSystem>
    | Pick<SiteElectricalSystem, "idx" | "importGridConnectionId">
): string => {
  const gridConnectionId = esys.importGridConnectionId;
  return esysCount === 1
    ? ""
    : gridConnectionId
      ? ` ${gridConnectionId.slice(-3)}`
      : ` ${parseFloat(esys.idx as string) + 1}`;
};

export const getEsysForSdmMatch = (
  site: Site,
  sdmMatchConfig: SdmMatchConfig
): SiteElectricalSystem => {
  for (const siteEsys of Object.values(site.esys).filter((e) => !!e)) {
    if (
      (sdmMatchConfig.buyerId === site.id &&
        sdmMatchConfig.buyerEsysUuid === siteEsys.uuid) ||
      (sdmMatchConfig.sellerId === site.id &&
        sdmMatchConfig.sellerEsysUuid === siteEsys.uuid)
    ) {
      return siteEsys;
    }
  }
  throw new Error(
    "No matching electrical system found for this site and match config"
  );
};
